import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles, siteInfo } from '@/mocks/data';

const articleImages: Record<string, string> = {
  'welcome-bonus-2026': 'https://readdy.ai/api/search-image?query=Elegant%20luxury%20editorial%20photography%20with%20warm%20golden%20and%20dark%20tones%2C%20sophisticated%20atmosphere%2C%20premium%20entertainment%20theme%2C%20rich%20contrast%20and%20soft%20lighting%2C%20high-end%20aesthetic%2C%20magazine%20quality&width=1200&height=600&seq=article-bonus-2026&orientation=landscape',
  'sports-betting-guide': 'https://readdy.ai/api/search-image?query=Dynamic%20sports%20stadium%20atmosphere%20with%20golden%20evening%20light%2C%20elegant%20editorial%20photography%20style%2C%20dark%20sophisticated%20undertones%2C%20premium%20sports%20theme%2C%20dramatic%20lighting%20and%20rich%20contrast&width=1200&height=600&seq=article-sports-2026&orientation=landscape',
  'live-casino-experience': 'https://readdy.ai/api/search-image?query=Elegant%20casino%20table%20with%20warm%20golden%20lighting%2C%20sophisticated%20dark%20atmosphere%2C%20premium%20entertainment%20setting%2C%20professional%20editorial%20photography%2C%20rich%20contrast%20and%20soft%20shadows&width=1200&height=600&seq=article-live-2026&orientation=landscape',
  'slot-games-top10': 'https://readdy.ai/api/search-image?query=Vibrant%20slot%20machine%20gaming%20visual%20with%20golden%20lights%20and%20luxurious%20casino%20atmosphere%2C%20dark%20elegant%20background%2C%20premium%20entertainment%20aesthetic%2C%20rich%20colors%20and%20dramatic%20lighting%2C%20editorial%20quality&width=1200&height=600&seq=article-slots-2026&orientation=landscape',
  'payment-guide': 'https://readdy.ai/api/search-image?query=Secure%20digital%20payment%20concept%20with%20golden%20lock%20and%20shield%20elements%20on%20dark%20sophisticated%20background%2C%20premium%20financial%20technology%20theme%2C%20elegant%20golden%20accents%2C%20professional%20editorial%20style&width=1200&height=600&seq=article-payment-2026&orientation=landscape',
  'responsible-gaming': 'https://readdy.ai/api/search-image?query=Calm%20mindfulness%20concept%20with%20soft%20golden%20light%20and%20serene%20atmosphere%20on%20dark%20elegant%20background%2C%20wellness%20and%20balance%20theme%2C%20sophisticated%20minimal%20aesthetic%2C%20warm%20and%20gentle%20visual&width=1200&height=600&seq=article-responsible-2026&orientation=landscape',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const otherArticles = articles.filter((a) => a.slug !== slug).slice(0, 3);
  const imageUrl = slug ? articleImages[slug] : articleImages['welcome-bonus-2026'];

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - ${siteInfo.name}`;
    } else {
      document.title = `文章未找到 - ${siteInfo.name}`;
    }
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-foreground-500">抱歉，您访问的文章不存在。</p>
          <Link to="/" className="mt-4 inline-block text-primary-500 font-medium hover:underline">返回首页</Link>
        </div>
      </div>
    );
  }

  const categoryLabel =
    article.category === 'sports' ? '体育竞技' :
    article.category === 'live-casino' ? '真人娱乐' :
    article.category === 'slots' ? '电子游艺' : '优惠活动';

  const jsonLdArticle = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteInfo.url}/article/${slug}`,
    },
  };

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: categoryLabel, path: `/category/${article.category}` },
    { label: article.title },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLdArticle)}</script>
      <main className="min-h-screen bg-background-50">
        <div className="pt-16 md:pt-18">
          <Breadcrumb items={breadcrumbItems} />

          <article className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-4xl mx-auto">
              <div className="mb-6 md:mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <Link
                    to={`/category/${article.category}`}
                    className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium hover:bg-secondary-200 transition-colors"
                  >
                    {categoryLabel}
                  </Link>
                  <span className="text-xs text-foreground-400">{article.date}</span>
                  <span className="text-xs text-foreground-400">{article.author}</span>
                </div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight" style={{ fontFamily: "var(--font-heading)" }}>
                  {article.title}
                </h1>
                <p className="mt-3 text-sm md:text-base text-foreground-500 leading-relaxed">
                  {article.summary}
                </p>
              </div>

              <div className="relative h-64 md:h-96 rounded-2xl overflow-hidden mb-8 md:mb-10">
                <img
                  src={imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                  prose-h2:text-xl md:prose-h2:text-2xl prose-h2:font-bold prose-h2:text-foreground-900 prose-h2:mt-8 prose-h2:mb-4
                  prose-p:text-sm md:prose-p:text-base prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                  prose-ul:list-disc prose-ul:pl-5 prose-ul:mb-4
                  prose-ol:list-decimal prose-ol:pl-5 prose-ol:mb-4
                  prose-li:text-sm md:prose-li:text-base prose-li:text-foreground-700 prose-li:mb-2
                  prose-strong:text-foreground-900 prose-strong:font-semibold"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 md:mt-12 pt-8 border-t border-background-200">
                <h3 className="text-lg font-bold text-foreground-900 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                  相关文章
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                  {otherArticles.map((rel) => (
                    <Link
                      key={rel.slug}
                      to={`/article/${rel.slug}`}
                      className="group block bg-background-100 rounded-xl p-4 hover:-translate-y-1 transition-all duration-200"
                    >
                      <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
                        {rel.title}
                      </h4>
                      <p className="text-xs text-foreground-500 line-clamp-2">{rel.summary}</p>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </article>
        </div>
      </main>
    </>
  );
}